import { useAuth } from "@/_core/hooks/useAuth";
import { SitePageShell } from "@/components/SiteHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getLoginUrl } from "@/const";
import { trpc } from "@/lib/trpc";
import { INQUIRY_STATUS_LABEL, INQUIRY_TYPE_LABEL } from "@shared/inquiry";
import { ORDER_STATUS_LABEL } from "@shared/orderStatus";
import { formatKRW } from "@shared/pricing";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";
import { Link } from "wouter";

export default function MyPage() {
  const { user, isAuthenticated, loading } = useAuth();
  const orders = trpc.orders.listMine.useQuery(undefined, {
    enabled: isAuthenticated,
  });
  const inquiries = trpc.inquiries.listMine.useQuery(undefined, {
    enabled: isAuthenticated,
  });

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated) {
      window.location.href = getLoginUrl();
    }
  }, [loading, isAuthenticated]);

  if (loading || !isAuthenticated) {
    return (
      <SitePageShell>
        <div className="container py-20 flex justify-center">
          <Loader2 className="animate-spin text-muted-foreground" />
        </div>
      </SitePageShell>
    );
  }

  return (
    <SitePageShell>
      <div className="container py-10 md:py-14">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-accent-foreground/80 mb-2">
              My Studio
            </div>
            <h1 className="font-serif text-3xl text-primary">마이페이지</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {user?.email ?? user?.name}님, 안녕하세요.
            </p>
          </div>
          <Button asChild variant="outline">
            <Link href="/create">새 작품 만들기</Link>
          </Button>
        </div>

        <h2 className="font-serif text-xl text-primary mb-3">주문 내역</h2>

        {orders.isLoading ? (
          <div className="py-16 flex justify-center">
            <Loader2 className="animate-spin text-muted-foreground" />
          </div>
        ) : !orders.data?.length ? (
          <Card>
            <CardContent className="py-16 text-center text-muted-foreground">
              <div className="mb-4">아직 주문 내역이 없습니다.</div>
              <Button asChild>
                <Link href="/create">첫 작품 만들기</Link>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {orders.data.map((o) => (
              <Card key={o.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4 flex gap-4 items-center">
                  <img
                    src={o.imageUrl}
                    alt={o.orderNo}
                    className="w-20 h-20 object-cover rounded-sm"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-muted-foreground">
                      {o.orderNo}
                    </div>
                    <div className="text-sm text-primary font-medium">
                      {ORDER_STATUS_LABEL[o.status]}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {new Date(o.createdAt).toLocaleString("ko-KR")}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-serif text-lg text-primary">
                      {formatKRW(o.totalAmount)}
                    </div>
                    <div className="flex gap-1.5 justify-end mt-1">
                      <Button asChild size="sm" variant="ghost">
                        <Link href={`/inquiry/new?orderId=${o.id}`}>문의하기</Link>
                      </Button>
                      <Button asChild size="sm" variant="outline">
                        <Link href={`/orders/${o.id}`}>상세보기</Link>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <h2 className="font-serif text-xl text-primary mt-10 mb-3">내 문의</h2>
        {inquiries.isLoading ? (
          <div className="py-8 flex justify-center">
            <Loader2 className="animate-spin text-muted-foreground" />
          </div>
        ) : !inquiries.data?.length ? (
          <Card>
            <CardContent className="py-8 text-center text-sm text-muted-foreground">
              접수한 문의가 없습니다. 궁금한 점은 주문 카드의 "문의하기"로 남겨 주세요.
              <div className="mt-2">
                <Link href="/support" className="underline underline-offset-2 hover:text-primary">
                  고객센터 / FAQ
                </Link>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {inquiries.data.map((q) => (
              <Card key={q.id}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span>{INQUIRY_TYPE_LABEL[q.type]}</span>
                    <span>·</span>
                    <span className="font-mono">{q.orderNo}</span>
                    <span>·</span>
                    <span>{new Date(q.createdAt).toLocaleString("ko-KR")}</span>
                    <span
                      className={
                        "ml-auto px-2 py-0.5 rounded-full font-medium " +
                        (q.status === "answered"
                          ? "bg-green-100 text-green-800"
                          : "bg-yellow-100 text-yellow-800")
                      }
                    >
                      {INQUIRY_STATUS_LABEL[q.status]}
                    </span>
                  </div>
                  <div className="text-sm font-medium text-primary">{q.title}</div>
                  <div className="text-sm text-muted-foreground whitespace-pre-wrap break-words">
                    {q.content}
                  </div>
                  {q.answer && (
                    <div className="mt-2 rounded-md bg-muted/50 p-3">
                      <div className="text-xs text-muted-foreground mb-1">
                        관리자 답변
                        {q.answeredAt ? ` · ${new Date(q.answeredAt).toLocaleString("ko-KR")}` : ""}
                      </div>
                      <div className="text-sm whitespace-pre-wrap break-words">{q.answer}</div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </SitePageShell>
  );
}
